/**
 * Program first stop (accelerators-row): a founder who came in through a
 * PREMIER program's lander lands on their canvas with that program's
 * application open, while the Big Idea is written in the background. A
 * landing-tier lander's founder gets the normal canvas.
 *
 * Pure and cross-compatible. The lander, the sign-up redirect, /apply, the
 * canvas and Lev's first-canvas welcome all decide through
 * `programFirstStop`; if they disagreed, Lev would describe a view the canvas
 * does not show.
 */
import { ACCELERATOR_ROW_PROGRAMS } from "@/constants/acceleratorsRow";
import type { AppFormatId } from "@/constants/accelerators/applications";
import { PROGRAM_APPLICATIONS } from "@/constants/programApplications";
import { PROGRAMS, programFromConversionSource, type ProgramSlug } from "@/constants/programs";

/** Only a premier program's lander has a first stop. */
export function isPremierProgramFormat(formatId: AppFormatId): boolean {
  return PROGRAM_APPLICATIONS[formatId]?.tier === "premier";
}

/**
 * Which first-stop view Lev's first-canvas welcome describes: the Programs
 * view with the program unconfirmed, or the program's application after the
 * founder confirmed it before the welcome went out.
 */
export type ProgramFirstStopWelcome = "programs" | "application";

export interface ProgramFirstStopFlags {
  /** `accelerators-straight-in` for the project owner. */
  straightIn: boolean;
  /** `accelerators-row` for the project owner. */
  rowEnabled: boolean;
  /** Formats launched for the project owner. */
  launchedFormatIds: readonly AppFormatId[];
}

/**
 * The lander program whose first stop the canvas shows, or null when the
 * canvas opens as usual. Every flag is the owner's: the row is drawn for the
 * owner, so a stop the owner cannot see is no stop.
 */
export function programFirstStop(
  conversionSource: unknown,
  flags: ProgramFirstStopFlags,
): ProgramSlug | null {
  const slug = programFromConversionSource(conversionSource);
  if (!slug || !flags.straightIn || !flags.rowEnabled) return null;
  const formatId = PROGRAMS[slug].appFormatId;
  if (!isPremierProgramFormat(formatId)) return null;
  return flags.launchedFormatIds.includes(formatId) ? slug : null;
}

/** The Accelerators row program that holds a lander program's application. */
export function rowProgramIdForSlug(slug: ProgramSlug): string | null {
  const formatId = PROGRAMS[slug].appFormatId;
  return ACCELERATOR_ROW_PROGRAMS.find((p) => p.formatId === formatId)?.id ?? null;
}

/**
 * The full-screen onboarding overlay. At the first stop the Big Idea is
 * written behind the Programs view instead of under the overlay.
 */
export function showsOnboardingOverlay(args: {
  isLoading: boolean;
  initialGenerating: boolean;
  hasBigIdea: boolean;
  programFirstStop: boolean;
}): boolean {
  return (
    args.isLoading || (args.initialGenerating && !args.hasBigIdea && !args.programFirstStop)
  );
}

/**
 * Whether the Big Idea arrival reveal plays. At the first stop it never does:
 * it would open the Big Idea over the founder's Programs view or application.
 * Same for an explicit Programs visit (the home page's Accelerators
 * announcement banner's `?programs=` link, no program landed on): normalizing
 * that link's empty program id to "no first stop" (useAcceleratorsRow) left
 * both first-stop guards false, so the reveal still fired and raced the
 * Programs view the founder actually came to see.
 */
export function arrivalRevealPlays(
  decision: { reveal: boolean },
  programFirstStop: boolean,
  explicitProgramsVisit = false,
): boolean {
  return decision.reveal && !programFirstStop && !explicitProgramsVisit;
}

/**
 * The narrowest viewport where the Accelerators lightbox docks beside an open
 * Lev panel (Tailwind `sm`, AcceleratorsLightbox's `sm:right-[...]`). Below
 * it Lev's panel covers the lightbox.
 */
const LIGHTBOX_DOCKS_BESIDE_LEV_MIN_WIDTH = 640;

/**
 * Whether the first stop opens with Lev collapsed to its pill: on a phone an
 * open Lev panel would hide the Programs view the founder must confirm in.
 */
export function firstStopDefersLev(viewportWidth: number): boolean {
  return viewportWidth < LIGHTBOX_DOCKS_BESIDE_LEV_MIN_WIDTH;
}
